import type {Maybe} from "../../bootstrap/common/types";
import {noop} from "./functions";
import {GLOBAL} from "../globals";

export const MINIMUM_PRESS_DURATION_MILLIS = 100;

export class TouchControlHandler {
    private posX: number = 0;
    private touchTarget: Maybe<Element> = undefined;
    private touchLeftCallback: (event: Event, target: Element, index: number) => void = noop;
    private touchRightCallback: (event: Event, target: Element, index: number) => void = noop;
    private touchEndCallback: (event: Event, target: Element, index: number) => void = noop;
    private touchLongPressCallback: (event: Event, target: Element, index: number) => void = noop;
    private longPressTimeoutId: Maybe<number> = undefined;
    private longPressTriggered: boolean = false;

    public constructor(private elements: Element[]) {
    }

    public onTouchLeft(callback: (event: Event, target: Element, index: number) => void): this {
        this.touchLeftCallback = callback;
        return this;
    }

    public onTouchRight(callback: (event: Event, target: Element, index: number) => void): this {
        this.touchRightCallback = callback;
        return this;
    }

    public onTouchEnd(callback: (event: Event, target: Element, index: number) => void): this {
        this.touchEndCallback = callback;
        return this;
    }

    public onTouchLongPress(callback: (event: Event, target: Element, index: number) => void): this {
        this.touchLongPressCallback = callback;
        return this;
    }


    public activate(): void {
        this.elements.forEach((e, i) => {
            e.addEventListener("touchstart", event => this.touchStart(event, i));
            e.addEventListener("touchend", event => this.touchEnd(event, i));
            e.addEventListener("touchcancel", _ => this.touchCancel());
        });
    }

    private touchStart(event: Event, index: number): void {
        this.touchTarget = event.currentTarget instanceof Element ? event.currentTarget : undefined;
        if (!this.touchTarget) {
            return;
        }
        if (event instanceof TouchEvent && event.type === "touchstart") {
            const touches = event.touches;
            if (touches?.length === 1) {
                this.longPressTriggered = false;
                this.longPressTimeoutId = GLOBAL.window().setTimeout(() => {
                    this.longPressTriggered = true;
                    this.touchLongPressCallback(event, this.touchTarget!, index);
                }, MINIMUM_PRESS_DURATION_MILLIS);
            }
        }
        this.posX = this.determinePosX(event);
    }

    private touchEnd(event: Event, index: number): void {
        if (!this.touchTarget) {
            return;
        }
        if (this.longPressTriggered) {
            this.touchEndCallback(event, this.touchTarget, index);
            this.longPressTriggered = false;
            this.clearLongPress();
            return;
        }
        if (this.isTouchLeft()) {
            this.touchLeftCallback(event, this.touchTarget, index);
            this.touchTarget = undefined;
            this.clearLongPress();
        } else if (this.isTouchRight()) {
            this.touchRightCallback(event, this.touchTarget, index);
            this.touchTarget = undefined;
            this.clearLongPress();
        }
    }

    private touchCancel(): void {
        this.clearLongPress();
        this.touchTarget = undefined;
        this.posX = 0;
    }

    private determinePosX(event: Event): number {
        if (!(event instanceof TouchEvent) || event.touches.length === 0) {
            return 0;
        }
        return event.touches[0].clientX ?? 0;
    }

    private isTouchLeft(): boolean {
        if (!this.touchTarget) {
            return false;
        }
        const rect = this.touchTarget.getBoundingClientRect();
        return rect.left <= this.posX && this.posX - rect.left <= rect.width / 2;
    }

    private isTouchRight(): boolean {
        if (!this.touchTarget) {
            return false;
        }
        const rect = this.touchTarget.getBoundingClientRect();
        return rect.right >= this.posX && rect.right - this.posX <= rect.width / 2;
    }

    private clearLongPress(): void {
        GLOBAL.window().clearTimeout(this.longPressTimeoutId);
        this.longPressTimeoutId = undefined;
    }
}
